import { useQuery } from "@tanstack/react-query";

import { cn } from "@/marketing/lib/utils";
import { PRODUCT_QUERIES } from "@/marketing/infrastructure/product/utils/queries";
import type {
  InfluencerCampaign,
  ProductAllocation,
} from "@/marketing/models/influencerCampaign";

import {
  buildCampaignProductSelectionState,
  canSelectCampaignProductSku,
} from "./campaignProductSelection";

interface ProductChoiceGridProps {
  campaign: InfluencerCampaign;
  selectedSkus: string[];
  onChange: (nextSelectedSkus: string[]) => void;
}

export function ProductChoiceGrid({
  campaign,
  selectedSkus,
  onChange,
}: ProductChoiceGridProps) {
  const selectionState = buildCampaignProductSelectionState(
    campaign,
    selectedSkus,
  );
  const productsQuery = useQuery(
    PRODUCT_QUERIES.bySkus(selectionState.poolSkus),
  );

  const handleToggleSku = (sku: string) => {
    if (selectedSkus.includes(sku)) {
      onChange(selectedSkus.filter((selected) => selected !== sku));
      return;
    }
    if (!canSelectCampaignProductSku(campaign, selectedSkus, sku)) return;
    onChange([...selectedSkus, sku]);
  };

  if (productsQuery.isPending) {
    return (
      <p className="tw-py-6 tw-text-center tw-text-sm tw-text-muted-foreground">Loading products…</p>
    );
  }
  if (productsQuery.isError) {
    return (
      <p className="tw-py-6 tw-text-center tw-text-sm tw-text-destructive">Failed to load the campaign product pool.</p>
    );
  }

  const productsBySku = new Map(
    (productsQuery.data ?? []).map((product) => [product.sku, product]),
  );
  const uniqueAllocations = (campaign.productAllocations ?? []).filter(
    (allocation, index, allocations) =>
      allocations.findIndex((candidate) => candidate.sku === allocation.sku) ===
      index,
  );

  const productOption = (allocation: ProductAllocation) => {
    const product = productsBySku.get(allocation.sku);
    const productLabel = product?.color ?? product?.name ?? allocation.sku;
    const isSelected = selectedSkus.includes(allocation.sku);
    const unavailableReason = allocation.disabled
      ? "Unavailable"
      : product?.status === "outofstock"
        ? "Out of stock"
        : product?.status === "onbackorder"
          ? "On backorder"
          : product?.status !== "instock"
            ? "Unavailable"
            : undefined;
    const isSelectable = canSelectCampaignProductSku(
      campaign,
      selectedSkus,
      allocation.sku,
    );
    const isLocked = !isSelected && (!isSelectable || unavailableReason !== undefined);
    const accessibleLabel = unavailableReason
      ? `${productLabel} — ${unavailableReason}`
      : productLabel;

    return (
      <button
        key={allocation.sku}
        type="button"
        disabled={isLocked}
        aria-label={accessibleLabel}
        aria-pressed={isSelected}
        onClick={() => handleToggleSku(allocation.sku)}
        className={cn(
          "tw-flex tw-items-center tw-gap-3 tw-rounded-md tw-border tw-border-border tw-p-2 tw-text-left",
          isSelected && "tw-border-primary tw-bg-primary/10",
          isLocked && "tw-cursor-not-allowed tw-opacity-40",
        )}
      >
        {product?.productImageUrls[0] && (
          <img
            src={product.productImageUrls[0]}
            alt=""
            className="tw-h-12 tw-w-12 tw-rounded tw-object-cover"
          />
        )}
        <div className="tw-min-w-0 tw-flex-1">
          <div className="tw-truncate tw-text-sm tw-font-medium">{productLabel}</div>
          <div className="tw-truncate tw-text-[11px] tw-text-muted-foreground">
            {allocation.sku}
          </div>
        </div>
        {unavailableReason && (
          <span className="tw-text-[10px] tw-font-semibold tw-text-destructive">
            {unavailableReason}
          </span>
        )}
      </button>
    );
  };

  const productGroups = campaign.productGroups ?? [];
  return (
    <div className="tw-flex tw-max-h-[50vh] tw-flex-col tw-gap-3 tw-overflow-y-auto">
      {productGroups.length > 0
        ? productGroups.map((productGroup) => (
            <section key={productGroup.groupId} className="tw-grid tw-gap-2">
              <div className="tw-flex tw-items-center tw-justify-between">
                <h4 className="tw-text-xs tw-font-semibold">
                  {productGroup.name?.trim() || productGroup.groupId}
                </h4>
                <span className="tw-text-[10px] tw-text-muted-foreground">
                  Pick {productGroup.choiceCount}
                </span>
              </div>
              {uniqueAllocations
                .filter(
                  (allocation) => allocation.groupId === productGroup.groupId,
                )
                .map(productOption)}
            </section>
          ))
        : uniqueAllocations.map(productOption)}
    </div>
  );
}
